import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Check, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { customerSearchQueryOptions, normalizeCustomerSearch } from '../api/queries'
import type { Customer } from '../api/types'
import { SearchInput } from '@/components/search-input'

/** Search-as-you-type customer picker: company, contact, email or phone. */
export function CustomerSearch({
  active = true,
  selectedId,
  disabled,
  onSelect,
}: {
  active?: boolean
  selectedId?: number
  disabled?: boolean
  onSelect: (customer: Customer) => void
}) {
  const api = useQuoteBuilderApi()
  const [query, setQuery] = useState('')
  const normalizedQuery = normalizeCustomerSearch(query)
  const [searchQuery, setSearchQuery] = useState(normalizedQuery)
  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(normalizedQuery), 250)
    return () => window.clearTimeout(timer)
  }, [normalizedQuery])
  const customers = useQuery({
    ...customerSearchQueryOptions(api, searchQuery),
    enabled: active,
    placeholderData: keepPreviousData,
  })

  return (
    <div className="grid gap-2">
      <label className="relative block">
        <span className="sr-only">Search customers</span>
        <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <SearchInput
          className="bg-card pl-9"
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Company, contact, email or phone"
          type="search"
          value={query}
        />
      </label>
      <div aria-label="Customers" className="max-h-64 overflow-y-auto rounded-lg border bg-card p-1" role="listbox">
        {customers.isLoading ? <p className="px-3 py-4 text-center text-sm text-muted-foreground">Searching customers…</p> : null}
        {customers.isError ? <p className="px-3 py-4 text-center text-sm text-destructive">Customer search is unavailable.</p> : null}
        {customers.data?.map((customer) => {
          const selected = customer.id === selectedId
          return (
            <button
              aria-selected={selected}
              className={cn(
                'grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md px-3 py-2.5 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50',
                selected && 'bg-muted',
              )}
              disabled={disabled}
              key={customer.id}
              onClick={() => onSelect(customer)}
              role="option"
              type="button"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{customer.company_name}</span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {[customer.contact_name, customer.email].filter(Boolean).join(' · ')}
                </span>
              </span>
              {selected ? <Check aria-hidden="true" className="size-4" /> : null}
            </button>
          )
        })}
        {customers.data?.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-muted-foreground">No matching customers.</p>
        ) : null}
      </div>
    </div>
  )
}
